/**
 * Task Brief Review Component - Phase 0 Task Understanding
 *
 * Displays the AI-generated task understanding for user review and approval.
 * Allows users to approve, reject with feedback, or modify the brief.
 */

import React, { useState } from 'react';
import {
  CheckCircle,
  XCircle,
  Edit3,
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  Loader,
  CheckSquare,
  Target,
  FileText,
  Zap,
  Clock,
  DollarSign
} from 'lucide-react';
import { useTaskBriefApproval, useTaskBriefRejection } from '../hooks/useTaskBrief';
import TaskBriefModifyModal from './TaskBriefModifyModal';

/**
 * Section component with collapsible header
 */
function Section({ title, icon: Icon, children, defaultExpanded = true }) {
  const [expanded, setExpanded] = useState(defaultExpanded);

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full px-4 py-3 bg-gray-50 hover:bg-gray-100 flex items-center justify-between transition-colors"
      >
        <div className="flex items-center gap-2">
          <Icon size={18} className="text-blue-600" />
          <h3 className="font-medium text-gray-900">{title}</h3>
        </div>
        {expanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
      </button>
      {expanded && (
        <div className="p-4 bg-white">
          {children}
        </div>
      )}
    </div>
  );
}

/**
 * Deliverable item display
 */
function DeliverableItem({ deliverable, index }) {
  return (
    <div className="border border-gray-200 rounded-lg p-4">
      <div className="flex items-start justify-between mb-2">
        <div className="flex-1">
          <h4 className="font-medium text-gray-900 flex items-center gap-2">
            {deliverable.name}
            {deliverable.is_primary && (
              <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">Primary</span>
            )}
          </h4>
          <p className="text-sm text-gray-600 mt-1">{deliverable.description}</p>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3 mt-3 text-sm">
        <div>
          <span className="text-gray-500">Type:</span>
          <span className="ml-2 font-medium text-gray-900">{deliverable.type}</span>
        </div>
        <div>
          <span className="text-gray-500">Format:</span>
          <span className="ml-2 font-mono text-gray-900 bg-gray-100 px-2 py-0.5 rounded">{deliverable.format}</span>
        </div>
        {deliverable.filename && (
          <div className="col-span-2">
            <span className="text-gray-500">Filename:</span>
            <span className="ml-2 font-mono text-gray-900 bg-gray-100 px-2 py-0.5 rounded">{deliverable.filename}</span>
          </div>
        )}
        {deliverable.estimated_size && (
          <div>
            <span className="text-gray-500">Est. Size:</span>
            <span className="ml-2 text-gray-900">{deliverable.estimated_size}</span>
          </div>
        )}
      </div>
      {deliverable.structure && (
        <div className="mt-3 p-3 bg-gray-50 rounded">
          <div className="text-xs text-gray-500 mb-1">Structure:</div>
          <pre className="text-xs text-gray-900 overflow-x-auto">{JSON.stringify(deliverable.structure, null, 2)}</pre>
        </div>
      )}
    </div>
  );
}

/**
 * Main TaskBriefReview component
 */
export default function TaskBriefReview({ taskBriefData, onApprove, onReject, onModify, onCancel }) {
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showModifyModal, setShowModifyModal] = useState(false);
  const [rejectFeedback, setRejectFeedback] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);

  const briefId = taskBriefData.task_brief_id || taskBriefData.taskBriefId;
  const brief = taskBriefData.iteration?.brief || taskBriefData.brief;
  const iteration = taskBriefData.iteration;

  const { approving, approve } = useTaskBriefApproval(briefId);
  const { rejecting, reject } = useTaskBriefRejection(briefId);

  if (!brief) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
        <AlertTriangle size={20} className="inline mr-2" />
        Invalid task brief data
      </div>
    );
  }

  const handleApprove = async () => {
    setIsProcessing(true);
    try {
      const result = await approve(true); // proceed_to_decomposition = true
      if (result && onApprove) {
        onApprove(result);
      }
    } finally {
      setIsProcessing(false);
    }
  };

  const handleRejectSubmit = async () => {
    if (!rejectFeedback.trim()) {
      return;
    }

    setIsProcessing(true);
    try {
      const result = await reject(rejectFeedback);
      if (result && onReject) {
        setShowRejectModal(false);
        setRejectFeedback('');
        onReject(result);
      }
    } finally {
      setIsProcessing(false);
    }
  };

  const handleModified = (result) => {
    setShowModifyModal(false);
    if (onModify) {
      onModify(result);
    }
  };

  const confidence = brief.confidence_score || 0;
  const confidencePercent = Math.round(confidence * 100);
  const confidenceColor = confidence >= 0.8 ? 'text-green-600' : confidence >= 0.6 ? 'text-yellow-600' : 'text-red-600';

  return (
    <div className="bg-white rounded-lg shadow-lg max-w-4xl mx-auto">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-6 rounded-t-lg">
        <h2 className="text-2xl font-bold mb-2">Task Understanding Review</h2>
        <p className="text-blue-100">
          Review the AI's understanding of your task before proceeding to workflow creation
        </p>
        <div className="mt-4 flex items-center gap-4 text-sm">
          <div className="flex items-center gap-2">
            <span className="text-blue-200">Iteration:</span>
            <span className="font-medium">{taskBriefData.current_iteration || taskBriefData.currentIteration || 1}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-blue-200">Confidence:</span>
            <span className={`font-medium ${confidenceColor}`}>{confidencePercent}%</span>
          </div>
          {iteration?.model_used && (
            <div className="flex items-center gap-2">
              <span className="text-blue-200">Model:</span>
              <span className="font-medium text-xs">{iteration.model_used}</span>
            </div>
          )}
        </div>
      </div>

      <div className="p-6 space-y-4">
        {/* Task Summary */}
        <Section title="Task Summary" icon={FileText} defaultExpanded={true}>
          <div className="space-y-3">
            <div>
              <div className="text-sm text-gray-500 mb-1">Category</div>
              <div className="font-medium text-gray-900 capitalize">{brief.task_category?.replace('_', ' ')}</div>
            </div>
            <div>
              <div className="text-sm text-gray-500 mb-1">Summary</div>
              <div className="text-gray-900">{brief.task_summary}</div>
            </div>
          </div>
        </Section>

        {/* Objectives */}
        <Section title="Objectives" icon={Target} defaultExpanded={true}>
          <ul className="space-y-2">
            {brief.objectives?.map((obj, idx) => (
              <li key={idx} className="flex items-start gap-2">
                <CheckSquare size={18} className="text-blue-600 mt-0.5 flex-shrink-0" />
                <span className="text-gray-900">{obj}</span>
              </li>
            ))}
          </ul>
        </Section>

        {/* Deliverables */}
        <Section title="Expected Deliverables" icon={FileText} defaultExpanded={true}>
          <div className="space-y-3">
            {brief.deliverables?.map((deliverable, idx) => (
              <DeliverableItem key={idx} deliverable={deliverable} index={idx} />
            ))}
          </div>
        </Section>

        {/* Scope */}
        {brief.task_scope && (
          <Section title="Scope & Boundaries" icon={Target} defaultExpanded={false}>
            <div className="space-y-4">
              {brief.task_scope.in_scope && brief.task_scope.in_scope.length > 0 && (
                <div>
                  <h4 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
                    <CheckCircle size={16} className="text-green-600" />
                    In Scope
                  </h4>
                  <ul className="list-disc list-inside space-y-1 text-gray-700">
                    {brief.task_scope.in_scope.map((item, idx) => (
                      <li key={idx}>{item}</li>
                    ))}
                  </ul>
                </div>
              )}
              {brief.task_scope.out_of_scope && brief.task_scope.out_of_scope.length > 0 && (
                <div>
                  <h4 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
                    <XCircle size={16} className="text-red-600" />
                    Out of Scope
                  </h4>
                  <ul className="list-disc list-inside space-y-1 text-gray-700">
                    {brief.task_scope.out_of_scope.map((item, idx) => (
                      <li key={idx}>{item}</li>
                    ))}
                  </ul>
                </div>
              )}
              {brief.task_scope.assumptions && brief.task_scope.assumptions.length > 0 && (
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Assumptions</h4>
                  <ul className="list-disc list-inside space-y-1 text-gray-600">
                    {brief.task_scope.assumptions.map((item, idx) => (
                      <li key={idx}>{item}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </Section>
        )}

        {/* Success Criteria */}
        {brief.success_criteria && brief.success_criteria.length > 0 && (
          <Section title="Success Criteria" icon={CheckSquare} defaultExpanded={false}>
            <ul className="space-y-2">
              {brief.success_criteria.map((criterion, idx) => (
                <li key={idx} className="flex items-start gap-2">
                  <CheckCircle size={18} className="text-green-600 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-900">{criterion}</span>
                </li>
              ))}
            </ul>
          </Section>
        )}

        {/* Execution Approach */}
        {brief.approach && (
          <Section title="Execution Strategy" icon={Zap} defaultExpanded={false}>
            <div className="space-y-4">
              {brief.approach.strategy && (
                <div>
                  <div className="text-sm text-gray-500 mb-1">Strategy</div>
                  <div className="font-medium text-gray-900 capitalize">{brief.approach.strategy}</div>
                </div>
              )}
              {brief.approach.key_steps && brief.approach.key_steps.length > 0 && (
                <div>
                  <div className="text-sm text-gray-500 mb-2">Key Steps</div>
                  <ol className="list-decimal list-inside space-y-1 text-gray-700">
                    {brief.approach.key_steps.map((step, idx) => (
                      <li key={idx}>{step}</li>
                    ))}
                  </ol>
                </div>
              )}
            </div>
          </Section>
        )}

        {/* Resource Estimates */}
        {brief.estimates && (
          <Section title="Resource Estimates" icon={Clock} defaultExpanded={false}>
            <div className="grid grid-cols-2 gap-4 text-sm">
              {brief.estimates.complexity && (
                <div>
                  <div className="text-gray-500 mb-1">Complexity</div>
                  <div className="font-medium text-gray-900 capitalize">{brief.estimates.complexity}</div>
                </div>
              )}
              {brief.estimates.estimated_nodes && (
                <div>
                  <div className="text-gray-500 mb-1">Estimated Tasks</div>
                  <div className="font-medium text-gray-900">{brief.estimates.estimated_nodes} nodes</div>
                </div>
              )}
              {brief.estimates.estimated_cost_usd !== undefined && (
                <div>
                  <div className="text-gray-500 mb-1 flex items-center gap-1">
                    <DollarSign size={14} />
                    Estimated Cost
                  </div>
                  <div className="font-medium text-gray-900">${brief.estimates.estimated_cost_usd.toFixed(4)}</div>
                </div>
              )}
              {brief.estimates.estimated_duration_minutes && (
                <div>
                  <div className="text-gray-500 mb-1 flex items-center gap-1">
                    <Clock size={14} />
                    Estimated Duration
                  </div>
                  <div className="font-medium text-gray-900">{brief.estimates.estimated_duration_minutes} minutes</div>
                </div>
              )}
            </div>
          </Section>
        )}

        {/* Uncertainties & Risks */}
        {((brief.uncertainties && brief.uncertainties.length > 0) || (brief.risks && brief.risks.length > 0)) && (
          <Section title="Uncertainties & Risks" icon={AlertTriangle} defaultExpanded={false}>
            <div className="space-y-4">
              {brief.uncertainties && brief.uncertainties.length > 0 && (
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Uncertainties</h4>
                  <ul className="list-disc list-inside space-y-1 text-gray-700">
                    {brief.uncertainties.map((item, idx) => (
                      <li key={idx}>{item}</li>
                    ))}
                  </ul>
                </div>
              )}
              {brief.risks && brief.risks.length > 0 && (
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Risks</h4>
                  <ul className="list-disc list-inside space-y-1 text-gray-700">
                    {brief.risks.map((item, idx) => (
                      <li key={idx}>{item}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </Section>
        )}

        {/* Clarification Questions */}
        {brief.clarification_questions && brief.clarification_questions.length > 0 && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <h4 className="font-medium text-yellow-900 mb-3 flex items-center gap-2">
              <AlertTriangle size={18} />
              Questions for Clarification
            </h4>
            <div className="space-y-3">
              {brief.clarification_questions.map((q, idx) => (
                <div key={idx} className="bg-white border border-yellow-200 rounded p-3">
                  <div className="font-medium text-gray-900 mb-1">{q.question}</div>
                  <div className="text-sm text-gray-600 mb-2">Why: {q.why_asking}</div>
                  {q.suggested_answer && (
                    <div className="text-sm text-gray-700">
                      <span className="font-medium">Suggested:</span> {q.suggested_answer}
                    </div>
                  )}
                  <div className="text-xs text-gray-500 mt-2">Impact if unanswered: {q.impact_if_unanswered}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-3 pt-4 border-t border-gray-200">
          <button
            onClick={handleApprove}
            disabled={approving || isProcessing}
            className="flex-1 px-6 py-3 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
          >
            {approving || isProcessing ? (
              <>
                <Loader size={18} className="animate-spin" />
                <span>Processing...</span>
              </>
            ) : (
              <>
                <CheckCircle size={18} />
                <span>Approve & Create Workflow</span>
              </>
            )}
          </button>
          <button
            onClick={() => setShowRejectModal(true)}
            disabled={rejecting || isProcessing}
            className="px-6 py-3 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
          >
            <XCircle size={18} />
            <span>Reject</span>
          </button>
          <button
            onClick={() => setShowModifyModal(true)}
            disabled={isProcessing}
            className="px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
          >
            <Edit3 size={18} />
            <span>Modify</span>
          </button>
          {onCancel && (
            <button
              onClick={onCancel}
              disabled={isProcessing}
              className="px-6 py-3 bg-gray-200 text-gray-700 font-medium rounded-lg hover:bg-gray-300 transition-colors"
            >
              Cancel
            </button>
          )}
        </div>
      </div>

      {/* Modify Modal */}
      {showModifyModal && (
        <TaskBriefModifyModal
          taskBriefData={taskBriefData}
          onClose={() => setShowModifyModal(false)}
          onModified={handleModified}
        />
      )}

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Provide Feedback</h3>
            <p className="text-gray-600 mb-4">
              Please explain what's wrong with this understanding. The AI will re-analyze your task with this feedback.
            </p>
            <textarea
              value={rejectFeedback}
              onChange={(e) => setRejectFeedback(e.target.value)}
              placeholder="e.g., 'The deliverables are missing X' or 'The scope should include Y'..."
              rows={4}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 resize-none"
            />
            <div className="flex gap-3 mt-4">
              <button
                onClick={handleRejectSubmit}
                disabled={!rejectFeedback.trim() || rejecting || isProcessing}
                className="flex-1 px-4 py-2 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {rejecting || isProcessing ? 'Processing...' : 'Submit Feedback'}
              </button>
              <button
                onClick={() => {
                  setShowRejectModal(false);
                  setRejectFeedback('');
                }}
                disabled={rejecting || isProcessing}
                className="px-4 py-2 bg-gray-200 text-gray-700 font-medium rounded-lg hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
